import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { fullDate, formatUnclippedMoney, inr, inrCompact, num } from '@/lib/format'
import { compareSortValues } from '@/lib/sort'
import { useNarrowViewport } from '@/lib/use-narrow-viewport'
import { CaretDown, CaretUp } from '@/components/icons'
import { ChannelTag } from '@/components/channel-tag'
import { StatusPill } from '@/components/status-pill'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tip } from '@/components/ui/tooltip'
import type { Order, Vendor } from '@/lib/vision/schemas'
import { cancelCountdown, type CancelCountdownTone } from './cancel-countdown'
import { vendorLabel } from './vendor-label'
import {
  cycleSortParam,
  orderDetailHref,
  parseSortParam,
  SERVER_SORT_FIELD,
  type SortDir,
} from './url-state'
import { poListFulfilment } from './po-list-fulfilment'

// Header cells stay beneath the app bar while the page itself scrolls.
const STICKY_HEAD = 'sticky-table-head'

// Cancels is Vision `cancelBy`. Sent / Pending are release-accounting totals
// from fulfillment waves, not the B2B true-shipped projection.
const COLUMN_COUNT = 10

/**
 * Client-side sort accessors for the current page. Vision pages `createdAt`
 * and `orderedAt` server-side; every other key reorders the fetched page here.
 */
const SORT_ACCESSORS: Record<string, (order: Order) => string | number | null> = {
  externalOrderNumber: order => order.externalOrderNumber,
  channel: order => order.channel,
  vendor: order => order.vendorName ?? order.vendor ?? null,
  shipTo: order => order.shipToLocationCode ?? null,
  orderedAt: order => (order.orderedAt ? Date.parse(order.orderedAt) : null),
  cancelBy: order => (order.cancelBy ? Date.parse(order.cancelBy) : null),
  orderedUnits: order => poListFulfilment(order).orderedQty,
  // Retain the bookmarked URL key; its value is canonical sent-to-warehouse.
  shippedUnits: order => poListFulfilment(order).sentQty,
  pendingUnits: order => poListFulfilment(order).remainingQty,
  status: order => order.status,
}

export function sortOrderRows(rows: Order[], sort: string): Order[] {
  const { field, dir } = parseSortParam(sort)
  if (field === SERVER_SORT_FIELD) return rows
  const accessor = SORT_ACCESSORS[field]
  if (!accessor) return rows
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => compareSortValues(accessor(a), accessor(b)) * sign)
}

interface OrdersTableProps {
  rows: Order[]
  vendors: Vendor[]
  loading: boolean
  emptyState: React.ReactNode
  /**
   * Row the keyboard cursor is on, or -1 when the operator has not started
   * keyboard navigation. Nothing is highlighted until they do — a pre-selected
   * first row reads as a state the operator never chose.
   */
  focusedIndex: number
  onFocusRow: (index: number) => void
  sort: string
  onSort: (sort: string | null) => void
  layout?: 'auto' | 'table' | 'cards'
  selectedOrderIds?: ReadonlySet<string>
  onToggleSelection?: (order: Order) => void
  isSelectable?: (order: Order) => boolean
  /** Why a row checkbox is disabled — shown as title / tooltip. */
  selectionDisabledReason?: (order: Order) => string | undefined
  pageSelectionChecked?: boolean | 'indeterminate'
  pageSelectionDisabled?: boolean
  pageSelectionTitle?: string
  onTogglePageSelection?: () => void
}

export function OrdersTable({
  rows,
  vendors,
  loading,
  emptyState,
  focusedIndex,
  onFocusRow,
  sort,
  onSort,
  layout = 'auto',
  selectedOrderIds,
  onToggleSelection,
  isSelectable,
  selectionDisabledReason,
  pageSelectionChecked = false,
  pageSelectionDisabled = false,
  pageSelectionTitle,
  onTogglePageSelection,
}: OrdersTableProps) {
  const navigate = useNavigate()
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([])
  const cardRefs = useRef<Array<HTMLElement | null>>([])
  const active = parseSortParam(sort)
  const cycle = (field: string) => onSort(cycleSortParam(sort, field))
  const narrow = useNarrowViewport()
  const cards = layout === 'cards' || (layout === 'auto' && narrow)

  useEffect(() => {
    if (focusedIndex < 0) return
    const row = cards ? cardRefs.current[focusedIndex] : rowRefs.current[focusedIndex]
    if (row) row.focus({ preventScroll: false })
  }, [focusedIndex, cards])

  if (cards) {
    return (
      <div className="flex flex-col gap-2">
        {loading && rows.length === 0
          ? Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="mt-2 h-3 w-48" />
              </Card>
            ))
          : null}
        {!loading && rows.length === 0 ? emptyState : null}
        {rows.map((order, index) => (
          <OrderCard
            key={order.id}
            order={order}
            vendors={vendors}
            index={index}
            focused={index === focusedIndex}
            onFocus={onFocusRow}
            onOpen={externalOrderNumber =>
              navigate(orderDetailHref(externalOrderNumber, order.channel))
            }
            selected={selectedOrderIds?.has(order.id) ?? false}
            selectable={isSelectable?.(order) ?? true}
            selectionDisabledReason={selectionDisabledReason?.(order)}
            onToggleSelection={onToggleSelection}
            cardRef={el => {
              cardRefs.current[index] = el
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <Card>
      <Table containerClassName="overflow-visible">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {onToggleSelection ? (
              <TableHead className={cn(STICKY_HEAD, 'w-12')} scope="col">
                <div className="flex h-5 items-center justify-center">
                  <Checkbox
                    checked={pageSelectionChecked}
                    disabled={pageSelectionDisabled || !onTogglePageSelection}
                    title={pageSelectionTitle}
                    aria-label="Select all pending Amazon POs on this page"
                    onCheckedChange={() => onTogglePageSelection?.()}
                  />
                </div>
              </TableHead>
            ) : null}
            <SortableTh
              field="externalOrderNumber"
              label="PO number"
              active={active}
              onCycle={cycle}
            />
            <SortableTh field="channel" label="Channel" active={active} onCycle={cycle} />
            <SortableTh field="vendor" label="Vendor" active={active} onCycle={cycle} />
            <SortableTh field="shipTo" label="Ship-to" active={active} onCycle={cycle} />
            <SortableTh field="orderedAt" label="PO date" active={active} onCycle={cycle} />
            <SortableTh field="cancelBy" label="Cancels" active={active} onCycle={cycle} />
            <SortableTh
              field="orderedUnits"
              label="Ordered"
              active={active}
              onCycle={cycle}
              align="right"
            />
            <SortableTh
              field="shippedUnits"
              label="Sent"
              active={active}
              onCycle={cycle}
              align="right"
            />
            <SortableTh
              field="pendingUnits"
              label="Pending"
              active={active}
              onCycle={cycle}
              align="right"
            />
            <SortableTh field="status" label="Status" active={active} onCycle={cycle} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && rows.length === 0
            ? Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} columns={COLUMN_COUNT + (onToggleSelection ? 1 : 0)} />
              ))
            : null}
          {!loading && rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={COLUMN_COUNT + (onToggleSelection ? 1 : 0)} className="p-0">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((order, index) => (
            <OrderRow
              key={order.id}
              order={order}
              vendors={vendors}
              index={index}
              focused={index === focusedIndex}
              onFocus={onFocusRow}
              onOpen={externalOrderNumber =>
                navigate(orderDetailHref(externalOrderNumber, order.channel))
              }
              selected={selectedOrderIds?.has(order.id) ?? false}
              selectable={isSelectable?.(order) ?? true}
              selectionDisabledReason={selectionDisabledReason?.(order)}
              onToggleSelection={onToggleSelection}
              rowRef={el => {
                rowRefs.current[index] = el
              }}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

function SortableTh({
  field,
  label,
  active,
  onCycle,
  align = 'left',
}: {
  field: string
  label: string
  active: { field: string; dir: SortDir }
  onCycle: (field: string) => void
  align?: 'left' | 'right'
}) {
  const isActive = active.field === field
  const ariaSort = !isActive ? 'none' : active.dir === 'asc' ? 'ascending' : 'descending'
  const Caret = isActive && active.dir === 'desc' ? CaretDown : CaretUp

  return (
    <TableHead
      className={cn(STICKY_HEAD, align === 'right' && 'text-right')}
      scope="col"
      aria-sort={ariaSort}
    >
      <button
        type="button"
        onClick={() => onCycle(field)}
        aria-label={`Sort by ${label}`}
        className={cn(
          'group/sort inline-flex items-center gap-0.5 rounded-sm uppercase tracking-wider hover:text-[color:var(--foreground)]',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
          align === 'right' && 'w-full justify-end',
          isActive && 'text-[color:var(--primary)]',
        )}
      >
        {label}
        <Caret
          aria-hidden
          className={cn(
            'size-3 shrink-0',
            isActive ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-40',
          )}
        />
      </button>
    </TableHead>
  )
}

interface OrderRowProps {
  order: Order
  vendors: Vendor[]
  index: number
  focused: boolean
  onFocus: (index: number) => void
  onOpen: (externalOrderNumber: string) => void
  selected: boolean
  selectable: boolean
  selectionDisabledReason?: string
  onToggleSelection?: (order: Order) => void
  rowRef: (el: HTMLTableRowElement | null) => void
}

function OrderRow({
  order,
  vendors,
  index,
  focused,
  onFocus,
  onOpen,
  selected,
  selectable,
  selectionDisabledReason,
  onToggleSelection,
  rowRef,
}: OrderRowProps) {
  const totals = useMemo(() => poListFulfilment(order), [order])
  const lines = order.items.length

  const poDate = fullDate(order.orderedAt)

  const vendor = vendorLabel(order, vendors)

  const poTip = (
    <div className="flex flex-col gap-0.5 text-left text-[11.5px]">
      {vendor ? (
        <span className="font-semibold text-[color:var(--foreground)]">{vendor}</span>
      ) : (
        <span className="text-[color:var(--muted-foreground)]">No vendor on the payload</span>
      )}
      <span className="tabular-nums text-[color:var(--muted-foreground)]">
        {num(lines)} line{lines === 1 ? '' : 's'} · {num(totals.orderedQty)} units ·{' '}
        {inr(totals.orderedValueRupees)}
      </span>
    </div>
  )

  return (
    <TableRow
      ref={rowRef}
      tabIndex={0}
      role="row"
      aria-selected={focused}
      data-state={selected ? 'selected' : undefined}
      onFocus={() => onFocus(index)}
      onClick={() => onOpen(order.externalOrderNumber)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(order.externalOrderNumber)
        }
      }}
      className={cn(
        'group cursor-pointer outline-none',
        // Keyboard cursor: a quiet wash, no ring. Pointer users never see it.
        focused && 'bg-[color:var(--muted)]',
        'focus-visible:bg-[color:var(--muted)]',
      )}
    >
      {onToggleSelection ? (
        <TableCell
          className="text-center"
          onClick={event => event.stopPropagation()}
          onKeyDown={event => event.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            disabled={!selectable}
            onCheckedChange={() => onToggleSelection(order)}
            aria-label={`Select ${order.externalOrderNumber} for bulk acknowledgement`}
            title={
              selectable
                ? 'Add to bulk acknowledgement'
                : (selectionDisabledReason ?? 'This PO cannot be acknowledged in bulk')
            }
          />
        </TableCell>
      ) : null}
      <TableCell className="tabular-nums">
        <Tip tip={poTip}>
          <span className="font-semibold text-[color:var(--foreground)]">
            {order.externalOrderNumber}
          </span>
        </Tip>
      </TableCell>
      <TableCell>
        <ChannelTag channel={order.channel} logoUrl={order.channelLogoUrl} />
      </TableCell>
      <TableCell className="text-[color:var(--foreground)]">{vendor || '—'}</TableCell>
      <TableCell className="tabular-nums text-[color:var(--muted-foreground)]">
        {order.shipToLocationCode ?? '—'}
      </TableCell>
      <TableCell className="text-[color:var(--muted-foreground)]">
        {order.orderedAt ? (
          <Tip tip={<span className="tabular-nums">{poDate}</span>}>
            <span className="tabular-nums whitespace-nowrap">{poDate}</span>
          </Tip>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="py-1.5">
        <CancelByCell iso={order.cancelBy} />
      </TableCell>
      <TableCell className="text-right">
        <QtyMoneyCell qty={totals.orderedQty} valueRupees={totals.orderedValueRupees} />
      </TableCell>
      <TableCell className="text-right">
        <QtyMoneyCell qty={totals.sentQty} valueRupees={totals.sentValueRupees} emptyDash />
      </TableCell>
      <TableCell className="text-right">
        <QtyMoneyCell qty={totals.remainingQty} valueRupees={totals.remainingValueRupees} />
      </TableCell>
      <TableCell>
        <StatusPill status={order.status} />
      </TableCell>
    </TableRow>
  )
}

function OrderCard({
  order,
  vendors,
  index,
  focused,
  onFocus,
  onOpen,
  selected,
  selectable,
  selectionDisabledReason,
  onToggleSelection,
  cardRef,
}: {
  order: Order
  vendors: Vendor[]
  index: number
  focused: boolean
  onFocus: (index: number) => void
  onOpen: (externalOrderNumber: string) => void
  selected: boolean
  selectable: boolean
  selectionDisabledReason?: string
  onToggleSelection?: (order: Order) => void
  cardRef: (el: HTMLElement | null) => void
}) {
  const totals = useMemo(() => poListFulfilment(order), [order])
  const vendor = vendorLabel(order, vendors)
  const poDate = order.orderedAt ? fullDate(order.orderedAt) : '—'

  return (
    <article
      ref={cardRef}
      tabIndex={0}
      role="button"
      aria-selected={focused}
      onFocus={() => onFocus(index)}
      onClick={() => onOpen(order.externalOrderNumber)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(order.externalOrderNumber)
        }
      }}
      className={cn(
        'min-h-[44px] cursor-pointer rounded-xl bg-[color:var(--card)] p-3 shadow-[var(--shadow-raised)] outline-none',
        'hover:bg-[color:var(--muted)]/60 focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
        focused && 'ring-2 ring-[color:var(--ring)]',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2">
          {onToggleSelection ? (
            <span onClick={event => event.stopPropagation()}>
              <Checkbox
                checked={selected}
                disabled={!selectable}
                onCheckedChange={() => onToggleSelection(order)}
                aria-label={`Select ${order.externalOrderNumber} for bulk acknowledgement`}
                title={
                  selectable
                    ? 'Add to bulk acknowledgement'
                    : (selectionDisabledReason ?? 'This PO cannot be acknowledged in bulk')
                }
              />
            </span>
          ) : null}
          <span className="font-semibold tabular-nums text-[color:var(--foreground)]">
            {order.externalOrderNumber}
          </span>
        </span>
        <StatusPill status={order.status} />
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[color:var(--muted-foreground)]">
        <ChannelTag channel={order.channel} logoUrl={order.channelLogoUrl} />
        <span>{vendor || '—'}</span>
        <span className="tabular-nums">{order.shipToLocationCode ?? '—'}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[color:var(--muted-foreground)]">
        <span className="tabular-nums">{poDate}</span>
        <CancelByCell iso={order.cancelBy} />
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
        <div>
          <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
            Ordered
          </dt>
          <dd className="font-semibold tabular-nums">
            {num(totals.orderedQty)}
            <div className="text-[10.5px] font-normal text-[color:var(--muted-foreground)]">
              {formatUnclippedMoney(totals.orderedValueRupees)}
            </div>
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
            Sent
          </dt>
          <dd className="font-semibold tabular-nums">
            {totals.sentQty == null || totals.sentQty === 0 ? (
              <span className="text-[color:var(--muted-foreground)]">—</span>
            ) : (
              <>
                {num(totals.sentQty)}
                <div className="text-[10.5px] font-normal text-[color:var(--muted-foreground)]">
                  {formatUnclippedMoney(totals.sentValueRupees)}
                </div>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
            Pending
          </dt>
          <dd className="font-semibold tabular-nums">
            {num(totals.remainingQty)}
            <div className="text-[10.5px] font-normal text-[color:var(--muted-foreground)]">
              {formatUnclippedMoney(totals.remainingValueRupees)}
            </div>
          </dd>
        </div>
      </dl>
    </article>
  )
}

function CancelByCell({ iso }: { iso: string | null | undefined }) {
  const countdown = cancelCountdown(iso)
  if (countdown.tone === 'missing') {
    return <span className="text-[color:var(--muted-foreground)]">—</span>
  }

  const deadline = fullDate(iso)
  return (
    <Tip tip={<span className="tabular-nums">{deadline}</span>}>
      <span
        aria-label={countdown.ariaLabel}
        className={cn(
          'inline-flex h-6 min-w-[2.25rem] items-center justify-center rounded px-1.5',
          'tabular-nums text-[12px] font-semibold whitespace-nowrap',
          cancelToneClass(countdown.tone),
        )}
      >
        {countdown.label}
      </span>
    </Tip>
  )
}

function cancelToneClass(tone: CancelCountdownTone): string {
  switch (tone) {
    case 'soon':
      return 'bg-[color:var(--warn-soft)] text-[color:var(--warn)]'
    case 'overdue':
      return 'bg-[color:var(--crit-soft)] text-[color:var(--crit)]'
    default:
      return 'text-[color:var(--muted-foreground)]'
  }
}

function QtyMoneyCell({
  qty,
  valueRupees,
  emptyDash = false,
}: {
  qty: number | null
  valueRupees: number | null
  emptyDash?: boolean
}) {
  if (emptyDash && (qty == null || qty === 0)) {
    return <span className="text-[color:var(--muted-foreground)]">—</span>
  }
  return (
    <div className="leading-tight">
      <div className="tabular-nums font-semibold text-[color:var(--foreground)]">{num(qty)}</div>
      <div className="tabular-nums text-[10.5px] text-[color:var(--muted-foreground)]">
        {inrCompact(valueRupees)}
      </div>
    </div>
  )
}

function SkeletonRow({ columns = COLUMN_COUNT }: { columns?: number } = {}) {
  return (
    <TableRow className="hover:bg-transparent">
      {Array.from({ length: columns }).map((_, index) => (
        <TableCell key={index}>
          <Skeleton className="h-3.5 w-full" />
        </TableCell>
      ))}
    </TableRow>
  )
}
